import { format, parseISO, isValid } from "date-fns";

// Datetimes from the API are naive local strings ("YYYY-MM-DDTHH:MM:SS").
// Trim to seconds so they parse as local wall-clock time without any TZ shift.
const parse = (value) => {
  if (!value) return null;
  const date = parseISO(String(value).substring(0, 19));
  return isValid(date) ? date : null;
};

/** e.g. "Apr 30" */
export const formatListDate = (value) => {
  const date = parse(value);
  return date ? format(date, "MMM d") : "";
};

/** e.g. "10:00 AM" */
export const formatListTime = (value) => {
  const date = parse(value);
  return date ? format(date, "h:mm a") : "";
};

const DATE_TIME_LONG = "MMM d, yyyy · h:mm a";

/** e.g. "Mar 28, 2026 · 2:10 PM" */
export const formatDateTimeLong = (value) => {
  const date = parse(value);
  return date ? format(date, DATE_TIME_LONG) : "";
};

/** e.g. "Sep 27, 8:00 AM" */
export const formatDateTimeShort = (value) => {
  const date = parse(value);
  return date ? format(date, "MMM d, h:mm a") : "";
};

// Timestamps with an offset ("...T03:00:00+00:00"), unlike the naive booking
// times above. Truncating them would show the offset's wall-clock time, so
// keep the offset and render the instant in the account's timezone.
export const parseInstant = (value) => {
  if (!value) return null;
  const date = parseISO(String(value));
  return isValid(date) ? date : null;
};

// The instant's wall-clock fields in timeZone, read straight from Intl.
// Rebuilding them as a Date would apply the browser's timezone and shift
// times in its DST gap. date-fns-tz would do this, but CRA's Jest can't load
// its ESM imports. Without a valid timeZone (profile not loaded yet), the
// browser's is used.
const PARTS_OPTIONS = {
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
};

const partsFormatter = (timeZone) => {
  try {
    return new Intl.DateTimeFormat("en-US", { ...PARTS_OPTIONS, timeZone });
  } catch {
    return new Intl.DateTimeFormat("en-US", PARTS_OPTIONS);
  }
};

const formatInstant = (value, timeZone, build) => {
  const date = parseInstant(value);
  if (!date) return "";
  const parts = Object.fromEntries(
    partsFormatter(timeZone)
      .formatToParts(date)
      .map(({ type, value: part }) => [type, part])
  );
  return build({
    date: `${parts.month} ${parts.day}, ${parts.year}`,
    time: `${parts.hour}:${parts.minute} ${parts.dayPeriod}`,
  });
};

/** e.g. "Mar 28, 2026, 11:30 AM", in the account's timezone */
export const formatTimestampLong = (value, timeZone) =>
  formatInstant(value, timeZone, ({ date, time }) => `${date}, ${time}`);

/** e.g. "Mar 28, 2026 · 2:10 PM", in the account's timezone */
export const formatTimestampDateTime = (value, timeZone) =>
  formatInstant(value, timeZone, ({ date, time }) => `${date} · ${time}`);

/** e.g. "Jun 2, 2026": the account-timezone calendar date of an instant */
export const formatTimestampDate = (value, timeZone) =>
  formatInstant(value, timeZone, ({ date }) => date);

/** e.g. "Mar 28, 2026" */
export const formatDateLong = (value) => {
  const date = parse(value);
  return date ? format(date, "MMM d, yyyy") : "";
};

/** e.g. "9:00 AM – 11:00 AM" */
export const formatTimeRange = (start, end) => {
  const startDate = parse(start);
  if (!startDate) return "";
  const startStr = format(startDate, "h:mm a");
  const endDate = parse(end);
  return endDate ? `${startStr} – ${format(endDate, "h:mm a")}` : startStr;
};
